import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { configApi } from '@/api/config-api'
import type { Config } from '@/types/config'
import { APISession, isValidAPISession } from '@/types/session'
import { useAuth } from '@/context/AuthContext'

const DEFAULT_CONFIG: Config = {
  PK: 'CONFIG',
  SK: 'SETTINGS',
  ActiveSession: '2024RS' as APISession,
  EnableSyncJob: false,
  Prompts: {
    LegislativeReview: null,
    Opposition: null,
    Support: null,
    ShouldMonitor: null
  },
  DataSource: 'Maryland'
}

export const useConfig = () => {
  const queryClient = useQueryClient()
  // Use optional chaining and defaults to prevent errors if AuthContext isn't available
  const { selectedOrganizationId = null, selectedWorkspaceId = null } = useAuth() || {}

  const { data, isPending, isError } = useQuery({
    queryKey: ['config', selectedOrganizationId, selectedWorkspaceId],
    queryFn: async () => {
      console.log('[useConfig] Fetching workspace config');
      
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        console.log('[useConfig] No workspace selected, returning default config');
        return DEFAULT_CONFIG;
      }
      
      try {
        const config = await configApi.getWorkspaceConfig(
          selectedOrganizationId,
          selectedWorkspaceId
        )
        
        // Validate API session format
        if (!config.ActiveSession || !isValidAPISession(config.ActiveSession)) {
          console.warn(`[useConfig] Invalid session format: ${config.ActiveSession}, using default`);
          config.ActiveSession = DEFAULT_CONFIG.ActiveSession
        }
        
        return config
      } catch (error) {
        console.error('[useConfig] Failed to load config:', error)
        throw error
      }
    },
    enabled: !!selectedOrganizationId && !!selectedWorkspaceId
  })

  const mutation = useMutation({
    mutationFn: async (data: Partial<Config>) => {
      if (!selectedOrganizationId || !selectedWorkspaceId) {
        throw new Error('No workspace selected')
      }
      
      console.log('[useConfig] Updating workspace config');
      return configApi.updateWorkspaceConfig(
        selectedOrganizationId,
        selectedWorkspaceId,
        data
      )
    },
    onSuccess: () => {
      // Invalidate both config and bills queries
      queryClient.invalidateQueries({ queryKey: ['config', selectedOrganizationId, selectedWorkspaceId] })
      queryClient.invalidateQueries({ queryKey: ['bills', null, null, selectedOrganizationId, selectedWorkspaceId] })
    },
    onError: (error) => {
      console.error('Failed to update config:', error)
      throw error
    }
  })

  return {
    config: data || DEFAULT_CONFIG,
    isLoading: isPending,
    error: isError ? 'Failed to load configuration. Using default settings.' : null,
    updateConfig: mutation
  }
}
